import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { peopleService, Person, PersonEncounter } from '../../services/peopleService';
import NotesService from '../../services/notesService';
import PageHeader from '../common/PageHeader';
import './PeopleDetail.css';

interface PeopleDetailProps {
    onToggleSidebar: () => void;
}

const PeopleDetail: React.FC<PeopleDetailProps> = ({ onToggleSidebar }) => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [person, setPerson] = useState<Person | null>(null);
    const [encounters, setEncounters] = useState<PersonEncounter[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDetails, setShowDetails] = useState(true);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [encounterDate, setEncounterDate] = useState<string>(() => {
        // Default to today's date in YYYY-MM-DD format
        return new Date().toISOString().split('T')[0];
    });
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (id && user) {
            fetchPersonAndEncounters();
        }
    }, [id, user]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuRef]);

    const fetchPersonAndEncounters = async () => {
        if (!id || !user) return;
        setLoading(true);
        try {
            const personId = parseInt(id);
            const personResponse = await peopleService.getPersonById(personId);
            if (personResponse.success) {
                setPerson(personResponse.person);
            } else {
                setError('Failed to fetch person details.');
            }

            const encountersResponse = await peopleService.getEncounters(personId, user.id);
            if (encountersResponse.success) {
                setEncounters(encountersResponse.encounters);
            } else {
                setError('Failed to fetch encounters.');
            }
        } catch (err) {
            setError('An error occurred.');
        } finally {
            setLoading(false);
        }
    };
    
    const canGoBack = (): boolean => {
        return window.history.length > 1;
    };

    const handleBack = () => {
        if (canGoBack()) {
            navigate(-1);
        } else {
            navigate('/people');
        }
    };

    const handleDeletePerson = async () => {
        if (!id) return;
        if (window.confirm('Are you sure you want to delete this person and all their encounters?')) {
            try {
                await peopleService.deletePerson(parseInt(id));
                navigate('/people');
            } catch (err) {
                setError('Failed to delete person.');
            }
        }
    };

    const sanitizePersonName = (name: string): string => {
        if (!name || typeof name !== 'string') {
            return '';
        }
        return name
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
            .substring(0, 50);
    };

    const handleAddEncounter = async () => {
        if (!id || !user || !person) return;
        
        try {
            setError(null);
            
            // Use the selected date (already in YYYY-MM-DD format)
            const dateStr = encounterDate;
            
            // Use title as note_id (backend will sanitize it automatically)
            const title = `${person.name} - ${dateStr}`;
            
            // Create a new note using the notes service
            const newNote = await NotesService.createNoteWithId(
                user.id,
                title,  // Use title as note_id
                title,  // Also use as title
                [], // Empty blocks array to start
                {} // Empty properties
            );
            
            // Fetch the latest person data to get current properties
            const personResponse = await peopleService.getPersonById(parseInt(id));
            if (!personResponse.success) {
                throw new Error('Failed to fetch person data');
            }
            
            const currentPerson = personResponse.person;
            const currentProperties = currentPerson.properties || {};
            const currentEncounters = currentProperties.encounters || [];
            
            // Add the new encounter to the encounters array
            // Use the note_id from the created note (sanitized by backend)
            const updatedEncounters = [
                ...currentEncounters,
                {
                    note_id: newNote.note_id,
                    encounter_date: dateStr
                }
            ];
            
            // Update the person's properties
            await peopleService.updatePerson(parseInt(id), {
                properties: {
                    ...currentProperties,
                    encounters: updatedEncounters
                }
            });
            
            // Navigate to the note editor page using the sanitized note_id
            navigate(`/notes/${newNote.note_id}`);
        } catch (err: any) {
            console.error('Error creating encounter:', err);
            setError(err.message || 'Failed to create encounter. Please try again.');
        }
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p className="error-message">{error}</p>;
    if (!person) return <p>Person not found.</p>;

    const leftActions = (
        <>
            {canGoBack() && (
                <button className="note-back-btn" onClick={handleBack}>
                    ← Back
                </button>
            )}
        </>
    );

    const rightActions = (
        <div className="people-detail-actions" ref={menuRef}>
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="btn-menu">
                ⋮
            </button>
            {isMenuOpen && (
                <div className="dropdown-menu">
                    <button onClick={() => { navigate(`/people/${id}/edit`); setIsMenuOpen(false); }} className="menu-item">Edit Person</button>
                    <button onClick={() => { handleDeletePerson(); setIsMenuOpen(false); }} className="menu-item danger">Delete Person</button>
                </div>
            )}
        </div>
    );

    return (
        <div className="people-detail-container">
            <PageHeader
                onToggleSidebar={onToggleSidebar}
                centerContent={<h1 className="page-header-title">{person.name}</h1>}
                leftActions={leftActions}
                rightActions={rightActions}
            />
            <div className="people-detail-content">
                <div className="details-section">
                    <button 
                        className="details-section-toggle" 
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        <span className="details-toggle-text">/details</span>
                        <span className="details-toggle-arrow">{showDetails ? '▼' : '▶'}</span>
                    </button>
                    <div className={`details-section-content ${showDetails ? 'expanded' : 'collapsed'}`}>
                        <div className="person-info-card">
                            <p><strong>Email:</strong> {person.email || 'N/A'}</p>
                            <p><strong>Phone:</strong> {person.phone || 'N/A'}</p>
                            <h2>General Notes</h2>
                            <p>{person.notes || 'No general notes.'}</p>
                        </div>
                    </div>
                </div>

                <div className="encounters-section">
                    <div className="encounters-header">
                        <h2>/encounters</h2>
                        <div className="encounters-header-controls">
                            <input
                                type="date"
                                value={encounterDate}
                                onChange={(e) => setEncounterDate(e.target.value)}
                                className="encounter-date-input"
                            />
                            <button className="btn" onClick={handleAddEncounter}>+ Add Encounter</button>
                        </div>
                    </div>
                    <div className="encounters-list">
                        {encounters.length > 0 ? (
                            encounters.map(encounter => (
                                <div key={encounter.note_id} className="encounter-card">
                                    <h3>{encounter.title}</h3>
                                    <p className="encounter-date">{new Date(encounter.created_at).toLocaleDateString()}</p>
                                    <div className="encounter-content">
                                        {/* Assuming blocks is an array of content blocks */}
                                        {encounter.blocks?.map((block: any) => <p key={block.block_id}>{block.text_value}</p>)}
                                    </div>
                                    <Link to={`/notes/${encounter.note_id}`} className="view-note-link">View full note</Link>
                                </div>
                            ))
                        ) : (
                            <p>No encounters recorded.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PeopleDetail;
